/**
 * Renders n8n executive summary markdown (BUB-134 v2.1) using Ant Design Typography.
 */

import React from "react";
import { Flex, Typography } from "antd";

const { Paragraph, Text, Title } = Typography;

const HEADER_PATTERN = /^(#{1,3})\s+(.+)$/;
const LIST_PATTERN = /^[-*]\s+(.+)$/;

function renderInlineMarkdown(text, keyPrefix) {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      return (
        <Text strong key={`${keyPrefix}-b-${index}`}>
          {part.slice(2, -2)}
        </Text>
      );
    }
    return part;
  });
}

/**
 * @param {string|null|undefined} format
 */
export function isExecutiveSummaryFormat(format) {
  if (!format || typeof format !== "string") return true;
  return format !== "structured_bullets";
}

const ExecutiveSummaryContent = ({ text, format = null, isMobile = false }) => {
  if (!text?.trim()) return null;

  if (!isExecutiveSummaryFormat(format)) {
    return (
      <Paragraph
        style={{
          marginBottom: 0,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          lineHeight: 1.6,
          fontSize: isMobile ? 14 : 15,
        }}
      >
        {text}
      </Paragraph>
    );
  }

  const lines = text.split("\n");
  const blocks = [];
  let listItems = [];

  const flushList = () => {
    if (listItems.length === 0) return;
    blocks.push(
      <Flex
        key={`list-${blocks.length}`}
        vertical
        gap={4}
        component="ul"
        style={{ margin: "0 0 8px", paddingLeft: 20 }}
      >
        {listItems.map((item, index) => (
          <Text
            key={`li-${index}`}
            component="li"
            style={{
              fontSize: isMobile ? 14 : 15,
              lineHeight: 1.6,
              display: "list-item",
            }}
          >
            {renderInlineMarkdown(item, `li-${index}`)}
          </Text>
        ))}
      </Flex>
    );
    listItems = [];
  };

  lines.forEach((line, lineIndex) => {
    const trimmed = line.trim();

    if (!trimmed) {
      flushList();
      return;
    }

    const headerMatch = trimmed.match(HEADER_PATTERN);
    if (headerMatch) {
      flushList();
      const level = headerMatch[1].length;
      const heading = headerMatch[2];
      blocks.push(
        <Title
          key={`h-${lineIndex}`}
          level={level === 1 ? 5 : level === 2 ? 5 : 5}
          style={{
            margin: lineIndex === 0 ? "0 0 6px" : "10px 0 6px",
            fontSize: level === 1 ? 16 : 15,
          }}
        >
          {renderInlineMarkdown(heading, `h-${lineIndex}`)}
        </Title>
      );
      return;
    }

    const listMatch = trimmed.match(LIST_PATTERN);
    if (listMatch) {
      listItems.push(listMatch[1]);
      return;
    }

    flushList();
    blocks.push(
      <Paragraph
        key={`p-${lineIndex}`}
        style={{
          marginBottom: 6,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          lineHeight: 1.6,
          fontSize: isMobile ? 14 : 15,
        }}
      >
        {renderInlineMarkdown(trimmed, `p-${lineIndex}`)}
      </Paragraph>
    );
  });

  flushList();

  return (
    <Flex vertical gap={2}>
      {blocks}
    </Flex>
  );
};

export default ExecutiveSummaryContent;
